import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Section, Box, Flex, TextField, TextArea, Text, Button, Table, Dialog, Select, Checkbox, Code } from '@radix-ui/themes';
import { PlusIcon, CodeIcon } from '@radix-ui/react-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';

import PageTitle from 'components/layout/PageTitle';
import { CheckBox, DatePickers } from 'components/form';
import { ConfirmDialog } from 'components/dialog';
import { instance } from 'api/axios.instance';
import { isFormUsed, toDateInput } from 'util/formVote';

const CATEGORIES = ['민원', '신청서', '보고서', '계획서', '기타'];
const FIELD_TYPES = ['text', 'number', 'date', 'datetime', 'longtext', 'select', 'phone', 'email'];
const NO_PLACEHOLDER_TYPES = new Set(['date', 'datetime', 'select']);

const PLACEHOLDER_HINTS = {
  phone: '예: 010-0000-0000',
  email: '예: email@example.com',
  number: '예: 0'
};

const DEFAULT_FIELD = () => ({
  marker: '{{필드명}}',
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  options: []
});

const defaultPeriod = () => {
  const start = new Date();
  const end = new Date();
  end.setMonth(end.getMonth() + 3);
  return {
    startDate: format(start, 'yyyy-MM-dd'),
    endDate: format(end, 'yyyy-MM-dd')
  };
};

const validationSchema = Yup.object({
  formCode: Yup.string().required('양식 코드를 입력해주세요.').max(50),
  formName: Yup.string().required('양식명을 입력해주세요.').max(200),
  startDate: Yup.string().required('사용 기간은 필수 입력 항목입니다.'),
  endDate: Yup.string().required('사용 기간은 필수 입력 항목입니다.')
});

export default function FormTemplateWrite() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!formId;

  const [isOpenSaveDialog, setIsOpenSaveDialog] = useState(false);
  const [fieldDefs, setFieldDefs] = useState([DEFAULT_FIELD()]);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [optionsEditIndex, setOptionsEditIndex] = useState(null);
  const [optionInput, setOptionInput] = useState('');
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState('');

  const formik = useFormik({
    initialValues: {
      formCode: '',
      formName: '',
      category: '',
      description: '',
      fieldDef: '[]',
      isUsed: true,
      ...defaultPeriod()
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: () => setIsOpenSaveDialog(true)
  });

  const fetchDetail = useCallback(async () => {
    const res = await instance(`/admin/form-template/${formId}`, { method: 'GET' });
    if (res.data.status === 200) {
      const d = res.data.data;
      formik.setValues({
        formCode: d.formCode || '',
        formName: d.formName || '',
        category: d.category || '',
        description: d.description || '',
        fieldDef: d.fieldDef || '[]',
        isUsed: isFormUsed(d),
        startDate: toDateInput(d.startDate),
        endDate: toDateInput(d.endDate)
      });
      try {
        const parsed = JSON.parse(d.fieldDef || '[]');
        setFieldDefs(parsed.length > 0 ? parsed : [DEFAULT_FIELD()]);
      } catch {
        setFieldDefs([DEFAULT_FIELD()]);
      }
    }
  }, [formId]);

  useEffect(() => {
    if (formId) fetchDetail();
  }, [formId, fetchDetail]);

  const updateField = (index, key, value) => setFieldDefs(prev => prev.map((f, i) => (i === index ? { ...f, [key]: value } : f)));

  const addRow = () => setFieldDefs(prev => [...prev, DEFAULT_FIELD()]);

  const markDeleted = index => setFieldDefs(prev => prev.map((f, i) => (i === index ? { ...f, _deleted: true } : f)));

  const restoreRow = index => setFieldDefs(prev => prev.map((f, i) => (i === index ? { ...f, _deleted: false } : f)));

  const addOptionToField = (fieldIndex, value) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setFieldDefs(prev => prev.map((f, i) => (i === fieldIndex ? { ...f, options: [...(f.options || []), trimmed] } : f)));
  };

  const removeOptionFromField = (fieldIndex, optionIndex) => {
    setFieldDefs(prev =>
      prev.map((f, i) => (i === fieldIndex ? { ...f, options: (f.options || []).filter((_, oi) => oi !== optionIndex) } : f))
    );
  };

  const openJsonDialog = () => {
    const active = fieldDefs.filter(f => !f._deleted).map(({ _deleted, ...rest }) => rest);
    setJsonText(JSON.stringify(active, null, 2));
    setJsonError('');
    setIsJsonDialogOpen(true);
  };

  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) {
        setJsonError('최상위 값은 배열([...]) 이어야 합니다.');
        return;
      }
      const normalized = parsed.map(f => ({
        marker: f.marker || '{{필드명}}',
        label: f.label || '',
        type: FIELD_TYPES.includes(f.type) ? f.type : 'text',
        required: !!f.required,
        placeholder: f.placeholder || '',
        options: Array.isArray(f.options) ? f.options : []
      }));
      setFieldDefs(normalized.length > 0 ? normalized : [DEFAULT_FIELD()]);
      setIsJsonDialogOpen(false);
      setJsonError('');
    } catch (e) {
      setJsonError(`JSON 파싱 오류: ${e.message}`);
    }
  };

  const handleSave = async () => {
    const activeFields = fieldDefs.filter(f => !f._deleted).map(({ _deleted, ...rest }) => rest);
    const payload = {
      ...formik.values,
      fieldDef: JSON.stringify(activeFields),
      useYn: formik.values.isUsed ? 'Y' : 'N'
    };
    const url = isEditMode ? `/admin/form-template/edit/${formId}` : '/admin/form-template/create';
    const res = await instance(url, { method: 'POST', data: payload });
    if (res.data.status === 201) navigate('/formTemplate');
  };

  return (
    <Section className="template-form-wrap">
      <PageTitle title={`양식 ${isEditMode ? '수정' : '등록'}`} />
      <form
        onSubmit={e => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <Box className="box box-white">
          <Table.Root className="table-write">
            <Table.Body>
              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">양식 코드</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextField.Root
                    name="formCode"
                    value={formik.values.formCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="예: MINWON_001"
                    readOnly={isEditMode}
                  />
                  {formik.touched.formCode && formik.errors.formCode && (
                    <Text as="p" className="font-error body3">
                      {formik.errors.formCode}
                    </Text>
                  )}
                </Table.Cell>
              </Table.Row>

              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">양식명</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextField.Root
                    name="formName"
                    value={formik.values.formName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="예: 민원 신청서"
                  />
                  {formik.touched.formName && formik.errors.formName && (
                    <Text as="p" className="font-error body3">
                      {formik.errors.formName}
                    </Text>
                  )}
                </Table.Cell>
              </Table.Row>

              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>사용여부</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Flex align="center">
                    <CheckBox
                      checked={formik.values.isUsed}
                      onCheckedChange={checked => formik.setFieldValue('isUsed', checked === true)}
                      id="form-is-used"
                    />
                    <Text as="label" htmlFor="form-is-used" color="gray">
                      사용
                    </Text>
                  </Flex>
                </Table.Cell>
              </Table.Row>

              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text className="required-start">사용 기간</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Flex align="center" gap="var(--space-2)">
                    <DatePickers
                      value={formik.values.startDate}
                      onChange={formik.handleChange('startDate')}
                      onBlur={formik.handleBlur('startDate')}
                    />
                    <Text color="gray">~</Text>
                    <DatePickers
                      value={formik.values.endDate}
                      onChange={formik.handleChange('endDate')}
                      onBlur={formik.handleBlur('endDate')}
                      min={formik.values.startDate}
                    />
                  </Flex>
                  {((formik.touched.startDate && formik.errors.startDate) || (formik.touched.endDate && formik.errors.endDate)) && (
                    <Text as="p" mt="var(--space-1)" className="font-error body3">
                      {formik.errors.startDate || formik.errors.endDate}
                    </Text>
                  )}
                </Table.Cell>
              </Table.Row>

              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>카테고리</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <Select.Root value={formik.values.category} onValueChange={v => formik.setFieldValue('category', v)}>
                    <Select.Trigger placeholder="카테고리 선택" />
                    <Select.Content position="popper">
                      {CATEGORIES.map(c => (
                        <Select.Item key={c} value={c}>
                          {c}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </Table.Cell>
              </Table.Row>

              <Table.Row align="center">
                <Table.ColumnHeaderCell colSpan={2}>
                  <Text>설명</Text>
                </Table.ColumnHeaderCell>
                <Table.Cell colSpan={10}>
                  <TextArea
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    placeholder="양식에 대한 설명을 입력하세요"
                    rows={3}
                  />
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>
        </Box>

        {/* 필드 정의 */}
        <Box className="box box-white">
          <Flex justify="between" align="center" mb="3">
            <Box>
              <Text as="h3" size="3" weight="bold">
                필드 정의
              </Text>
              <Text size="2" color="gray">
                양식에 포함할 입력 항목을 정의하세요.
              </Text>
            </Box>
            <Flex gap="2">
              <Button size="2" type="button" variant="outline" onClick={openJsonDialog}>
                <CodeIcon /> JSON 관리
              </Button>
              <Button size="2" type="button" variant="outline" onClick={addRow}>
                <PlusIcon /> 행 추가
              </Button>
            </Flex>
          </Flex>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell style={{ width: '22%' }}>마커</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '16%' }}>레이블</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '150px' }}>타입</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '60px', textAlign: 'center' }}>필수</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Placeholder / 선택항목</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '88px' }}>삭제</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {fieldDefs.map((field, index) => (
                <Table.Row
                  key={index}
                  style={
                    field._deleted
                      ? {
                          opacity: 0.45,
                          background: 'var(--gray-3)',
                          textDecoration: 'line-through'
                        }
                      : {}
                  }
                >
                  <Table.Cell>
                    <TextField.Root
                      size="2"
                      value={field.marker}
                      onChange={e => updateField(index, 'marker', e.target.value)}
                      placeholder="{{마커명}}"
                      style={{ fontFamily: 'monospace' }}
                      disabled={field._deleted}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <TextField.Root
                      size="2"
                      value={field.label}
                      onChange={e => updateField(index, 'label', e.target.value)}
                      disabled={field._deleted}
                    />
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden' }}>
                    <Select.Root value={field.type} onValueChange={v => updateField(index, 'type', v)} disabled={field._deleted}>
                      <Select.Trigger placeholder="선택" style={{ width: '100%' }} />
                      <Select.Content position="popper">
                        {FIELD_TYPES.map(t => (
                          <Select.Item key={t} value={t}>
                            {t}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex align="center" justify="center" style={{ paddingTop: 4 }}>
                      <Checkbox
                        size="2"
                        checked={!!field.required}
                        onCheckedChange={checked => updateField(index, 'required', checked === true)}
                        disabled={field._deleted}
                      />
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    {field.type === 'select' ? (
                      <Button size="2" type="button" variant="soft" disabled={field._deleted} onClick={() => setOptionsEditIndex(index)}>
                        옵션 관리 ({(field.options || []).length}개)
                      </Button>
                    ) : NO_PLACEHOLDER_TYPES.has(field.type) ? (
                      <TextField.Root size="2" disabled placeholder="-" />
                    ) : (
                      <TextField.Root
                        size="2"
                        value={field.placeholder || ''}
                        onChange={e => updateField(index, 'placeholder', e.target.value)}
                        disabled={field._deleted}
                        placeholder={PLACEHOLDER_HINTS[field.type] || ''}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {field._deleted ? (
                      <Button size="2" type="button" variant="outline" onClick={() => restoreRow(index)}>
                        되돌리기
                      </Button>
                    ) : (
                      <Button size="2" type="button" variant="outline" color="red" onClick={() => setDeletingIndex(index)}>
                        삭제
                      </Button>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>

        <Box>
          <Flex gap="2" justify="end">
            <Dialog.Root>
              <Dialog.Trigger>
                <Button size="2" variant="outline" type="button">
                  취소
                </Button>
              </Dialog.Trigger>
              <ConfirmDialog
                title="저장 취소"
                description="작성중인 내용을 저장하지 않고 나가시겠습니까?"
                onConfirm={() => navigate('/formTemplate')}
              />
            </Dialog.Root>
            <Button size="2" type="submit">
              {isEditMode ? '수정' : '저장'}
            </Button>
            <Dialog.Root open={isOpenSaveDialog} onOpenChange={setIsOpenSaveDialog}>
              <ConfirmDialog title="저장 확인" description="저장하시겠습니까?" onConfirm={handleSave} />
            </Dialog.Root>
          </Flex>
        </Box>

        {/* 삭제 확인 다이얼로그 */}
        <Dialog.Root
          open={deletingIndex !== null}
          onOpenChange={open => {
            if (!open) setDeletingIndex(null);
          }}
        >
          <ConfirmDialog
            title="삭제 확인"
            description="해당 필드를 삭제하시겠습니까? 저장 전까지 되돌리기가 가능합니다."
            onConfirm={() => {
              markDeleted(deletingIndex);
              setDeletingIndex(null);
            }}
          />
        </Dialog.Root>

        {/* JSON 관리 다이얼로그 */}
        <Dialog.Root
          open={isJsonDialogOpen}
          onOpenChange={open => {
            if (!open) setIsJsonDialogOpen(false);
          }}
        >
          <Dialog.Content maxWidth="640px">
            <Dialog.Title>필드 정의 JSON 관리</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="3">
              현재 필드 정의를 JSON으로 확인·편집할 수 있습니다. 수정 후 적용하면 테이블에 반영됩니다.
            </Dialog.Description>
            <Box mb="2">
              <Flex justify="end" gap="2" mb="2">
                <Button
                  size="1"
                  type="button"
                  variant="soft"
                  onClick={() => {
                    navigator.clipboard.writeText(jsonText).then(() => alert('클립보드에 복사됐습니다.'));
                  }}
                >
                  복사
                </Button>
              </Flex>
              <textarea
                value={jsonText}
                onChange={e => {
                  setJsonText(e.target.value);
                  setJsonError('');
                }}
                rows={20}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  padding: '12px',
                  borderRadius: '6px',
                  border: jsonError ? '1px solid var(--red-8)' : '1px solid var(--gray-6)',
                  background: 'var(--gray-1)',
                  resize: 'vertical',
                  lineHeight: 1.6,
                  boxSizing: 'border-box'
                }}
                spellCheck={false}
              />
              {jsonError && (
                <Text size="1" color="red" mt="1" style={{ display: 'block' }}>
                  {jsonError}
                </Text>
              )}
            </Box>
            <Box mb="2">
              <Text size="1" color="gray">
                사용 가능한 타입: <Code size="1">{FIELD_TYPES.join(', ')}</Code>
              </Text>
            </Box>
            <Flex gap="2" justify="end" mt="3">
              <Dialog.Close>
                <Button variant="outline" type="button" size="2">
                  닫기
                </Button>
              </Dialog.Close>
              <Button type="button" size="2" onClick={applyJson}>
                적용
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {/* 선택 항목 편집 다이얼로그 */}
        <Dialog.Root
          open={optionsEditIndex !== null}
          onOpenChange={open => {
            if (!open) {
              setOptionsEditIndex(null);
              setOptionInput('');
            }
          }}
        >
          <Dialog.Content maxWidth="440px">
            <Dialog.Title>선택 항목 편집</Dialog.Title>
            <Dialog.Description size="2" color="gray" mb="3">
              항목을 입력 후 Enter 또는 추가 버튼을 누르세요.
            </Dialog.Description>
            {optionsEditIndex !== null && (
              <>
                <Box mb="3" style={{ minHeight: 60, maxHeight: 260, overflowY: 'auto' }}>
                  {(fieldDefs[optionsEditIndex]?.options || []).length === 0 ? (
                    <Text size="2" color="gray">
                      등록된 항목이 없습니다.
                    </Text>
                  ) : (
                    <Flex direction="column" gap="1">
                      {(fieldDefs[optionsEditIndex].options || []).map((opt, oi) => (
                        <Flex
                          key={oi}
                          align="center"
                          justify="between"
                          style={{
                            padding: '6px 12px',
                            border: '1px solid var(--gray-5)',
                            borderRadius: 6,
                            background: 'var(--gray-1)'
                          }}
                        >
                          <Text size="2">
                            {oi + 1}. {opt}
                          </Text>
                          <Button
                            size="1"
                            type="button"
                            variant="ghost"
                            color="red"
                            onClick={() => removeOptionFromField(optionsEditIndex, oi)}
                          >
                            삭제
                          </Button>
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Box>
                <Flex gap="2" align="center">
                  <TextField.Root
                    size="2"
                    placeholder="항목 입력"
                    value={optionInput}
                    onChange={e => setOptionInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        if (optionInput.trim()) {
                          addOptionToField(optionsEditIndex, optionInput.trim());
                          setOptionInput('');
                        }
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    size="2"
                    type="button"
                    onClick={() => {
                      if (optionInput.trim()) {
                        addOptionToField(optionsEditIndex, optionInput.trim());
                        setOptionInput('');
                      }
                    }}
                  >
                    추가
                  </Button>
                </Flex>
              </>
            )}
            <Flex gap="2" justify="end" mt="4">
              <Dialog.Close>
                <Button variant="outline" type="button" size="2">
                  닫기
                </Button>
              </Dialog.Close>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>
      </form>
    </Section>
  );
}
